/**
 * Configure a note — the same panel, footer and discard prompt as a report
 * tile's, holding the two things a note is: its title and its text.
 *
 * WRITES NOTHING UNTIL APPLY. A note used to be edited in place — its title in
 * the tile's menu, its text in the tile — each committed on blur, so nothing
 * said whether a change had been taken. Here it is one draft and one Apply,
 * like every other tile, landing in the dashboard's own draft: "Save for
 * everyone" still persists it, and Cancel on the page still throws it away.
 */
import type { TextTile } from '@clockwise/semantic-core';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/custom/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { SettingsHint, SettingsSection } from './settings-section';
import type { NoteSettings } from './tile-editing';

interface Props {
  open: boolean;
  /** The note being configured, or `null` to add one. */
  tile: TextTile | null;
  onApply: (note: NoteSettings) => void;
  onClose: () => void;
}

export function NoteSettingsSheet({ open, tile, onApply, onClose }: Props) {
  const [initial] = useState<NoteSettings>(() => ({
    title: tile?.title ?? '',
    body: tile?.body ?? '',
  }));
  const [note, setNote] = useState<NoteSettings>(initial);
  const [confirming, setConfirming] = useState(false);

  const isNew = tile === null;
  const dirty = note.title !== initial.title || note.body !== initial.body;
  const empty = note.title.trim() === '' && note.body.trim() === '';
  const name = initial.title.trim();

  const blocked = isNew && empty;
  // A form, so Enter in the title applies — as the menu field it replaces did
  // — and ⌘/Ctrl+Enter does from the text, where a bare Enter is a new line.
  const submit = (event?: { preventDefault: () => void }) => {
    event?.preventDefault();
    if (!blocked) onApply(note);
  };

  const requestClose = () => {
    if (dirty) setConfirming(true);
    else onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(next) => !next && requestClose()}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>
            {isNew ? 'Add a note' : name ? `Configure “${name}”` : 'Configure note'}
          </SheetTitle>
          <SheetDescription className="sr-only">Title and text for this note.</SheetDescription>
        </SheetHeader>

        <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
          <div className="min-h-0 flex-1 divide-y overflow-auto">
            <SettingsSection label="Title" htmlFor="note-title">
              <Input
                id="note-title"
                value={note.title}
                maxLength={200}
                placeholder="Untitled"
                onChange={(e) => setNote({ ...note, title: e.target.value })}
              />
              <SettingsHint>The note’s heading. A section label needs nothing else.</SettingsHint>
            </SettingsSection>
            <SettingsSection label="Text" htmlFor="note-body">
              <Textarea
                id="note-body"
                value={note.body}
                maxLength={4000}
                // A floor, not a fixed size: the field grows with what is typed.
                className="min-h-40"
                placeholder="Write a note…"
                onChange={(e) => setNote({ ...note, body: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey))
                    e.currentTarget.form?.requestSubmit();
                }}
              />
              <SettingsHint>
                Plain text, shown as typed; line breaks are kept. ⌘/Ctrl+Enter applies.
              </SettingsHint>
            </SettingsSection>
          </div>

          <SheetFooter className="flex-row items-center justify-end gap-2 border-t px-6 py-3">
            {blocked && (
              <p className="mr-auto text-muted-foreground text-xs">
                Give the note a title or text.
              </p>
            )}
            <Button type="button" variant="ghost" onClick={requestClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={blocked}>
              {isNew ? 'Add to dashboard' : 'Apply'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
      <ConfirmDialog
        open={confirming}
        title={isNew ? 'Discard this note?' : 'Discard your changes to this note?'}
        description="What you changed here hasn’t been applied to the dashboard."
        confirmLabel="Discard"
        destructive
        onConfirm={() => {
          setConfirming(false);
          onClose();
        }}
        onCancel={() => setConfirming(false)}
      />
    </Sheet>
  );
}
